import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  Button,
  FormField,
  Input,
  StatusStepper,
  SubmitButton,
  SubmittingHint,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { Modal } from "@/components/ui/Modal";
import {
  commissionModelerApi,
  type BrokerCashAtClosingMode,
  type BrokerFeeMonthlyMode,
  type CommissionModelDto,
  type CommissionModelResult,
  type SaveCommissionModelRequest,
} from "../../api/commissionModeler";
import type { CommissionModelerFormCopy } from "../../api/commissionModelerCopy";
import {
  CASH_MODE_ORDER,
  COMMISSION_MODELER_COPY_FALLBACK,
  MODE_ORDER,
  SELECT_CLASS,
  STEP_COPY,
  STEP_KEYS,
  cashModeLabelOf,
  errorMessage,
  formatMoney,
  fractionToPercent,
  lifetimeWorkedExample,
  modeLabelOf,
  monthlyWorkedExample,
  percentToFraction,
} from "./copy";
import { TermsPanel, HeadlineFigures } from "./CommissionModelWorkup";
import { hasText, textOrUndefined } from "../../pages/prequalify/blankCopy";

// Broker Commission Modeler — the create/edit form.
//
// 🔴 TWO SEPARATE AMOUNT FIELDS PER ARRANGEMENT, NOT ONE. The real Deal card
// reuses a single input for both the percent and the flat amount because it is
// seeded once from a saved row and closed. A modeler is the opposite: switching
// modes to compare arrangements IS the tool. One shared field would mean "5"
// (five percent) becomes "$5/month" the moment the mode changes, and $1,500/month
// becomes 1,500% on the way back.
//
// That is FOUR strings, not two: the monthly fee has a percent and a flat, and so
// does cash at closing. They are four independent pieces of state for the one
// reason above, and the cash pair is not folded into the monthly pair — the two
// mode vocabularies are separate types on purpose (see BrokerCashAtClosingMode),
// and their percentages are taken on different bases.
//
// 🔴 AND NONE OF THEM IS EVER RE-SEEDED FROM A RESPONSE. The server coerces the
// monthly amount to 0 in `none` mode, and ignores the cash amount outside `flat`
// mode — correctly; there is no such amount in those modes — so a form that read
// its own fields back would silently erase a typed figure the moment the broker
// looked at what "no monthly fee" or "no cash at closing" does. All four strings
// are local state, seeded ONLY on open, and survive every mode change in between.
//
// 🔴 THE PREVIEW IS A SERVER CALL. Debounced POST /preview, never a second
// implementation of the arithmetic. Responsiveness costs nothing in authority
// that way, and the numbers in the preview are the numbers that get saved.
//
// Portal-local by the isolation rule: the design-system kit, the shared Modal
// (@/components/ui/ is not a forbidden prefix — precedent
// tabs/prequalification/NewPrequalificationWizard.tsx), and portal-relative
// imports. No React Query; the write goes through useSubmittingMutation.

/** How long the form sits still before it asks the server what it is worth. */
const PREVIEW_DEBOUNCE_MS = 400;

/** What Tab may reach. Deliberately no visibility filter: jsdom reports no
 *  layout, so an `offsetParent` test would empty this list in every test while
 *  looking correct in a browser. */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

export interface CommissionModelFormModalProps {
  open: boolean;
  /** Guarded by the form against closing mid-submit. */
  onClose: () => void;
  /** The model being edited, or null to create a new one. */
  model: CommissionModelDto | null;
  /** Called with the saved model so the shell can refresh its list and open the
   *  workup. */
  onSaved: (saved: CommissionModelDto) => void | Promise<void>;
  /** The resolved arrangement/field vocabulary. Optional and defaulted to the
   *  compiled-in copy so a caller that has not resolved the overlay — and every
   *  existing test — renders exactly today's words. */
  copy?: CommissionModelerFormCopy;
}

/** A blank number field reads as zero for the two that have a natural zero. A
 *  blank REQUIRED field is caught by validation before it reaches here. */
function num(value: string): number {
  const trimmed = value.trim();
  if (trimmed === "") return 0;
  return Number(trimmed);
}

function isNonNegative(value: string, { allowBlank }: { allowBlank: boolean }): boolean {
  const trimmed = value.trim();
  if (trimmed === "") return allowBlank;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0;
}

/** Whole-percent fields: blank-or-valid, and strictly under 100 — the fraction
 *  the server stores must be under 1, and it rejects anything else with the same
 *  message the real Deal card's validator uses. */
function isPercentValid(value: string, { allowBlank }: { allowBlank: boolean }): boolean {
  const trimmed = value.trim();
  if (trimmed === "") return allowBlank;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 && n < 100;
}

/** A worked example: the step's arithmetic, in the broker's own figures.
 *
 *  role="status" rather than a plain paragraph — the text changes underneath the
 *  reader as the debounced preview lands, and a silent swap is the one thing a
 *  screen-reader user cannot recover from. Same reasoning as the cash warning,
 *  and deliberately NOT the same styling: this is information, not a caution. */
function WorkedExample({
  children,
  testId,
}: {
  children: ReactNode;
  testId: string;
}) {
  return (
    <p
      role="status"
      data-testid={testId}
      className="m-0 rounded-md border border-border-light bg-white-warm p-space-3 text-small text-onyx"
    >
      {children}
    </p>
  );
}

export function CommissionModelFormModal({
  open,
  onClose,
  model,
  onSaved,
  copy = COMMISSION_MODELER_COPY_FALLBACK,
}: CommissionModelFormModalProps) {
  const [name, setName] = useState("");
  const [clientName, setClientName] = useState("");
  const [totalDealSize, setTotalDealSize] = useState("");
  const [downPayment, setDownPayment] = useState("");
  // `flat` rather than `none`, the same choice the wire shapes made and for the
  // same reason: before the mode existed a blank cash field meant "flat, $0", and
  // opening a new model straight onto `none` would swap a field the broker can
  // simply leave alone for one they have to go and find.
  const [cashMode, setCashMode] = useState<BrokerCashAtClosingMode>("flat");
  const [cashAtClosing, setCashAtClosing] = useState("");
  const [cashPercent, setCashPercent] = useState("");
  const [mode, setMode] = useState<BrokerFeeMonthlyMode>("percent_of_seller_share");
  // See the header note: four independent strings, never re-seeded mid-session.
  const [percentAmount, setPercentAmount] = useState("");
  const [flatAmount, setFlatAmount] = useState("");
  const [saleFeePercent, setSaleFeePercent] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [preview, setPreview] = useState<CommissionModelResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // The wizard's own cursor. A bare integer with hard-coded step semantics, the
  // PartnerBrandWizard precedent — an enum here would buy nothing and cost a
  // lookup at every comparison.
  const [stepIndex, setStepIndex] = useState(0);

  // Every open starts from the model being edited (or blank). The modal stays
  // mounted while closed — Modal returns null — so without this an abandoned
  // draft would still be sitting there on the next open.
  useEffect(() => {
    if (!open) return;
    setName(model?.name ?? "");
    setClientName(model?.clientName ?? "");
    setTotalDealSize(model ? String(model.totalDealSize) : "");
    setDownPayment(model ? String(model.downPayment) : "");
    setCashMode(model?.cashAtClosingMode ?? "flat");
    // Only into the field that mode owns, exactly like the monthly pair below —
    // the stored flat column can still hold a stale figure under a `none` or
    // `percent_of_down_payment` model, and showing it would show a number that
    // is not part of the arrangement.
    setCashAtClosing(
      model?.cashAtClosingMode === "flat" ? String(model.brokerCashAtClosing) : "",
    );
    setCashPercent(
      model?.cashAtClosingMode === "percent_of_down_payment" &&
        model.cashAtClosingPercent != null
        ? String(fractionToPercent(model.cashAtClosingPercent))
        : "",
    );
    setMode(model?.monthlyFeeMode ?? "percent_of_seller_share");
    // ×100 on read, ONCE, and only into the field that mode owns.
    setPercentAmount(
      model?.monthlyFeeMode === "percent_of_seller_share"
        ? String(fractionToPercent(model.monthlyFeeAmount))
        : "",
    );
    setFlatAmount(
      model?.monthlyFeeMode === "flat_monthly" ? String(model.monthlyFeeAmount) : "",
    );
    setSaleFeePercent(
      model?.saleFeePercent != null ? String(fractionToPercent(model.saleFeePercent)) : "",
    );
    setError(null);
    // An edit already has a server-computed workup in hand; showing it beats a
    // blank panel while the first debounced preview is still pending.
    setPreview(model?.result ?? null);
    setPreviewError(null);
    // Editing an existing model opens on Review: every answer is already there,
    // and walking a broker back through four steps they filled in last week to
    // change one number is the tool getting in the way. Creating starts at 0.
    setStepIndex(model ? STEP_KEYS.length - 1 : 0);
  }, [open, model]);

  // ── Validation ────────────────────────────────────────────────────────────

  const totalNum = num(totalDealSize);
  const downNum = num(downPayment);
  const cashNum = num(cashAtClosing);

  const nameValid = name.trim().length > 0;
  const totalValid =
    totalDealSize.trim() !== "" && Number.isFinite(totalNum) && totalNum > 0;
  const downValid =
    isNonNegative(downPayment, { allowBlank: true }) && (!totalValid || downNum <= totalNum);
  // Blank stays legal in flat mode — cash at closing is optional, and blank has
  // always meant $0. Percent mode is the one that requires a figure: having
  // chosen "% of down payment" there is no reading of an empty box.
  const cashValid =
    cashMode === "none"
      ? true
      : cashMode === "percent_of_down_payment"
        ? isPercentValid(cashPercent, { allowBlank: false })
        : isNonNegative(cashAtClosing, { allowBlank: true });
  const amountValid =
    mode === "none"
      ? true
      : mode === "percent_of_seller_share"
        ? isPercentValid(percentAmount, { allowBlank: false })
        : isNonNegative(flatAmount, { allowBlank: false });
  const saleValid = isPercentValid(saleFeePercent, { allowBlank: true });

  const valid = nameValid && totalValid && downValid && cashValid && amountValid && saleValid;

  // D16 — a SOFT warning. Cash out of the seller's pocket exceeding what the
  // buyer put in is usually a mistake, and occasionally the whole point of the
  // scenario. It is a modeling tool: let them model a bad idea and see it.
  //
  // FLAT MODE ONLY. A percentage under 100 of the down payment cannot exceed the
  // down payment, so in percent mode the condition is unreachable rather than
  // merely unlikely — and a warning that can never fire is one a reader learns to
  // stop trusting. The percent field's own "under 100" rule is what holds it.
  const cashExceedsDown =
    cashMode === "flat" &&
    totalValid &&
    cashValid &&
    downValid &&
    cashAtClosing.trim() !== "" &&
    cashNum > downNum;

  // ── Per-step gating ───────────────────────────────────────────────────────
  //
  // Next is blocked only by the fields the CURRENT step is showing. Gating it on
  // the whole form would make step 1 wait on a monthly fee the broker cannot see
  // yet, and gating it on nothing would let them walk to Review and find the
  // Save button dead with no indication which step is at fault.
  //
  // 🔴 The soft cash warning is deliberately NOT in here. D16 says it never
  // blocks; putting it in this array is the one edit that would silently turn it
  // into a block, so the test named for D16 asserts Next stays enabled with it
  // showing.
  //
  // `cashValid` IS in here and always was — it is a different thing. It rejects a
  // percent of 100 or more, which is a value the server cannot store rather than
  // an arrangement worth a second look. The warning above is now gated to flat
  // mode, so the two can never be confused for one another: in percent mode there
  // is a hard rule and no warning, in flat mode a warning and no hard rule.
  const stepValid: boolean[] = [
    nameValid,
    totalValid && downValid,
    amountValid,
    cashValid && saleValid,
    valid,
  ];

  const step = STEP_KEYS[stepIndex];
  const isReview = stepIndex === STEP_KEYS.length - 1;
  const canAdvance = stepValid[stepIndex] ?? false;

  const goNext = useCallback(() => {
    setError(null);
    setStepIndex((i) => Math.min(i + 1, STEP_KEYS.length - 1));
  }, []);
  const goBack = useCallback(() => {
    setError(null);
    setStepIndex((i) => Math.max(i - 1, 0));
  }, []);

  // The two worked examples, in the broker's own figures. Both are pure
  // functions of the preview, so they say nothing until the server has computed
  // something — see the null cases in copy.ts.
  const monthlyExample = monthlyWorkedExample(mode, preview);
  const lifetimeExample = lifetimeWorkedExample(preview);

  /** The review, grouped BY STEP rather than as one flat list of eight rows.
   *
   *  Grouping is what makes "Change" honest: three of these answers live on the
   *  figures step, and three separate Change buttons all landing on the same
   *  step would read as three different destinations. One per group, labelled
   *  with the step it returns to.
   *
   *  Built from the form's own state rather than the preview so it reads
   *  correctly even while a debounced preview is still in flight — the only
   *  derived value here is the note, which is a subtraction and not a model. */
  const reviewGroups: {
    step: number;
    rows: { label: string; value: string }[];
  }[] = [
    {
      step: 0,
      rows: [
        { label: "Model", value: name.trim() || "—" },
        { label: "Client", value: clientName.trim() || "Not set" },
      ],
    },
    {
      step: 1,
      rows: [
        { label: "Deal size", value: totalValid ? formatMoney(totalNum) : "—" },
        { label: "Down payment", value: formatMoney(downNum) },
        { label: "Note", value: totalValid ? formatMoney(totalNum - downNum) : "—" },
      ],
    },
    {
      step: 2,
      rows: [
        {
          label: "Monthly fee",
          value:
            mode === "percent_of_seller_share"
              ? `${num(percentAmount)}% of seller's monthly share`
              : mode === "flat_monthly"
                ? `${formatMoney(num(flatAmount))}/month`
                : modeLabelOf(copy, "none"),
        },
      ],
    },
    {
      step: 3,
      rows: [
        {
          label: copy.cashLabel.replace(" (USD)", ""),
          // The ARRANGEMENT, in the same shape as the monthly row above: the
          // percentage stands on its own rather than resolving to a dollar,
          // because the resolved dollar is the server's and the rail beside this
          // is already showing it.
          value:
            cashMode === "percent_of_down_payment"
              ? `${num(cashPercent)}% of down payment`
              : cashMode === "flat" && cashAtClosing.trim() !== ""
                ? formatMoney(cashNum)
                : "None",
        },
        {
          label: copy.saleLabel.replace(" (%)", ""),
          value: saleFeePercent.trim() === "" ? "None" : `${num(saleFeePercent)}%`,
        },
      ],
    },
  ];

  // ── The wire body ─────────────────────────────────────────────────────────
  // ÷100 on write, guarded — 5 → 0.05, and never 0.05000000000000001.

  const buildBody = useCallback(
    (): SaveCommissionModelRequest => ({
      name: name.trim(),
      clientName: clientName.trim() === "" ? null : clientName.trim(),
      totalDealSize: totalNum,
      downPayment: downNum,
      brokerCashAtClosing: cashNum,
      monthlyFeeMode: mode,
      monthlyFeeAmount:
        mode === "percent_of_seller_share"
          ? percentToFraction(num(percentAmount))
          : mode === "flat_monthly"
            ? num(flatAmount)
            : 0,
      saleFeePercent:
        saleFeePercent.trim() === "" ? null : percentToFraction(num(saleFeePercent)),
      cashAtClosingMode: cashMode,
      cashAtClosingPercent:
        cashMode === "percent_of_down_payment"
          ? percentToFraction(num(cashPercent))
          : null,
    }),
    [
      name,
      clientName,
      totalNum,
      downNum,
      cashNum,
      cashMode,
      cashPercent,
      mode,
      percentAmount,
      flatAmount,
      saleFeePercent,
    ],
  );

  // ── The live preview ──────────────────────────────────────────────────────
  // Debounced, cancelled on every change, and silent about its own failures
  // beyond one muted line: a preview that cannot be computed is not a reason to
  // stop the broker typing, and Save has its own error path.

  const body = valid ? buildBody() : null;
  // Serialised so the effect re-runs on a VALUE change rather than on every
  // render (buildBody returns a fresh object each time).
  const bodyKey = body ? JSON.stringify(body) : null;

  useEffect(() => {
    if (!open || !bodyKey) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const result = await commissionModelerApi.preview(
            JSON.parse(bodyKey) as SaveCommissionModelRequest,
          );
          if (!cancelled) {
            setPreview(result);
            setPreviewError(null);
          }
        } catch (err) {
          if (!cancelled) {
            setPreviewError(errorMessage(err, "Couldn't work that out just now."));
          }
        }
      })();
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, bodyKey]);

  // ── Save ──────────────────────────────────────────────────────────────────

  const save = useSubmittingMutation(
    () =>
      model
        ? commissionModelerApi.update(model.id, buildBody())
        : commissionModelerApi.create(buildBody()),
    {
      onSuccess: (saved) => {
        // Closed on the RAW prop, not the guarded wrapper below: `pending` is
        // still true while onSuccess runs, so the mid-submit guard would swallow
        // this and strand the broker on a form that has already saved.
        onClose();
        void onSaved(saved);
      },
      onError: (err) => setError(errorMessage(err, "Couldn't save that model. Try again.")),
    },
  );

  const busy = save.pending;

  // ── Focus ─────────────────────────────────────────────────────────────────
  // Modal has no focus management of any kind — no initial focus, no trap, no
  // restore — and it has ~230 importers and no owner, so the handling lives here
  // rather than in it.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    if (!open) return;
    firstFieldRef.current?.focus();
  }, [open]);

  // Every step change moves focus to the new step's heading. Without it a
  // keyboard or screen-reader user presses Next and focus stays on a button that
  // has just been replaced — the content changed and nothing announced it. The
  // heading carries tabIndex={-1} so it can take focus without joining the Tab
  // cycle. Skipped on the first frame: the name field already has focus there,
  // and stealing it back to a heading would be worse.
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      mountedRef.current = false;
      return;
    }
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    headingRef.current?.focus();
  }, [open, stepIndex]);

  /** Tab cycle, scoped to this form's own wrapper.
   *
   *  Modal's X button sits in its header, OUTSIDE this wrapper, so it is not in
   *  the cycle — which is why the footer always carries a real Cancel button.
   *  Escape and the backdrop still close, both through the same guarded
   *  handler. */
  function handleKeyDown(ev: KeyboardEvent<HTMLDivElement>) {
    if (ev.key !== "Tab") return;
    const root = containerRef.current;
    if (!root) return;
    const nodes = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement;
    if (ev.shiftKey) {
      if (active === first || !root.contains(active)) {
        ev.preventDefault();
        last.focus();
      }
      return;
    }
    if (active === last) {
      ev.preventDefault();
      first.focus();
    }
  }

  /** Guarded against closing mid-submit. All three of Modal's close routes
   *  (Escape, backdrop, X) funnel through this single prop, so a caller-side
   *  guard is complete rather than partial. */
  const handleRequestClose = useCallback(() => {
    if (busy) return;
    onClose();
  }, [busy, onClose]);

  return (
    <Modal
      open={open}
      onClose={handleRequestClose}
      dismissOnBackdropClick={false}
      title={model ? "Edit model" : "New model"}
      description="A private what-if. Nothing here is submitted to Phoenix, attached to a deal, or seen by anyone else."
      size="lg"
      footer={
        <>
          <Button
            variant="tertiary"
            onClick={handleRequestClose}
            disabled={busy}
            data-testid="commission-model-cancel"
          >
            Cancel
          </Button>
          {stepIndex > 0 && (
            <Button
              variant="secondary"
              onClick={goBack}
              disabled={busy}
              data-testid="commission-model-back"
            >
              Back
            </Button>
          )}
          {isReview ? (
            <SubmitButton
              type="button"
              variant="primary"
              pending={busy}
              pendingLabel="Saving…"
              disabled={!valid}
              onClick={() => {
                setError(null);
                void save.submit();
              }}
              data-testid="commission-model-save"
            >
              {model ? "Save changes" : "Save model"}
            </SubmitButton>
          ) : (
            <Button
              variant="primary"
              onClick={goNext}
              disabled={!canAdvance || busy}
              data-testid="commission-model-next"
            >
              Next
            </Button>
          )}
        </>
      }
    >
      <div
        ref={containerRef}
        onKeyDown={handleKeyDown}
        data-testid="commission-model-form"
        className="grid gap-space-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]"
      >
        <div className="flex flex-col gap-space-4">
          <StatusStepper
            steps={STEP_KEYS.map((k) => ({ key: k, label: STEP_COPY[k].label }))}
            currentIndex={stepIndex}
            label="Model progress"
            data-testid="commission-model-steps"
          />

          <header className="flex flex-col gap-space-1">
            <h3
              ref={headingRef}
              tabIndex={-1}
              className="m-0 font-display text-lead font-bold text-onyx outline-none"
              data-testid="commission-model-step-title"
            >
              {STEP_COPY[step].title}
            </h3>
            <p className="m-0 text-small text-gray-mid">{STEP_COPY[step].blurb}</p>
          </header>

          {step === "deal" && (
            <>
              <FormField label="Model name" htmlFor="cm-name" required>
                <Input
                  id="cm-name"
                  ref={firstFieldRef}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={busy}
                  autoComplete="off"
                  placeholder="Riverside Plumbing — 5% option"
                />
              </FormField>

              <FormField
                label="Client name"
                htmlFor="cm-client"
                helperText="Optional — just so you can tell your models apart."
              >
                <Input
                  id="cm-client"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  disabled={busy}
                  autoComplete="off"
                />
              </FormField>
            </>
          )}

          {step === "figures" && (
          <div className="grid gap-space-4 sm:grid-cols-2">
            <FormField label="Total deal size (USD)" htmlFor="cm-total" required>
              <Input
                id="cm-total"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={totalDealSize}
                onChange={(e) => setTotalDealSize(e.target.value)}
                disabled={busy}
                placeholder="0.00"
              />
            </FormField>

            <FormField
              label="Down payment (USD)"
              htmlFor="cm-down"
              helperText="What the buyer puts in. The rest becomes the note."
              error={
                !downValid && downPayment.trim() !== ""
                  ? "Must be zero or more, and no more than the total deal size."
                  : undefined
              }
            >
              <Input
                id="cm-down"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={downPayment}
                onChange={(e) => setDownPayment(e.target.value)}
                disabled={busy}
                placeholder="0.00"
              />
            </FormField>
          </div>
          )}

          {step === "monthly" && (
            <>
          <FormField label="Monthly fee" htmlFor="cm-mode">
            <select
              id="cm-mode"
              className={SELECT_CLASS}
              value={mode}
              onChange={(e) => setMode(e.target.value as BrokerFeeMonthlyMode)}
              disabled={busy}
            >
              {MODE_ORDER.map((m) => (
                <option key={m} value={m}>
                  {modeLabelOf(copy, m)}
                </option>
              ))}
            </select>
          </FormField>

          {mode === "percent_of_seller_share" && (
            <FormField
              label={textOrUndefined(copy.percentLabel)}
              htmlFor="cm-amount-pct"
              helperText={textOrUndefined(copy.percentHelper)}
              error={
                percentAmount.trim() !== "" && !amountValid
                  ? "Must be a percent under 100."
                  : undefined
              }
            >
              <Input
                id="cm-amount-pct"
                type="number"
                min="0"
                max="100"
                step="0.01"
                inputMode="decimal"
                value={percentAmount}
                onChange={(e) => setPercentAmount(e.target.value)}
                disabled={busy}
                placeholder="5"
              />
            </FormField>
          )}

          {mode === "flat_monthly" && (
            <FormField label={textOrUndefined(copy.flatLabel)} htmlFor="cm-amount-flat">
              <Input
                id="cm-amount-flat"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={flatAmount}
                onChange={(e) => setFlatAmount(e.target.value)}
                disabled={busy}
                placeholder="0.00"
              />
            </FormField>
          )}

          {monthlyExample && (
            <WorkedExample testId="commission-model-monthly-example">
              {monthlyExample}
            </WorkedExample>
          )}
            </>
          )}

          {step === "other" && (
            <>
          {/* The arrangement first, then the figure it needs — the same shape as
              the monthly step. The helper rides the SELECTOR rather than the
              amount because "paid directly seller → broker, not administered by
              Phoenix" is true of all three arrangements, including the one with
              no amount under it at all. */}
          <FormField
            label="Cash at closing"
            htmlFor="cm-cash-mode"
            helperText={textOrUndefined(copy.cashHelper)}
          >
            <select
              id="cm-cash-mode"
              className={SELECT_CLASS}
              value={cashMode}
              onChange={(e) => setCashMode(e.target.value as BrokerCashAtClosingMode)}
              disabled={busy}
            >
              {CASH_MODE_ORDER.map((m) => (
                <option key={m} value={m}>
                  {cashModeLabelOf(copy, m)}
                </option>
              ))}
            </select>
          </FormField>

          {cashMode === "flat" && (
            <FormField label={textOrUndefined(copy.cashLabel)} htmlFor="cm-cash">
              <Input
                id="cm-cash"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={cashAtClosing}
                onChange={(e) => setCashAtClosing(e.target.value)}
                disabled={busy}
                placeholder="0.00"
              />
            </FormField>
          )}

          {cashMode === "percent_of_down_payment" && (
            <FormField
              label={textOrUndefined(copy.cashPercentLabel)}
              htmlFor="cm-cash-pct"
              helperText={textOrUndefined(copy.cashPercentHelper)}
              error={
                cashPercent.trim() !== "" && !cashValid
                  ? "Must be a percent under 100."
                  : undefined
              }
            >
              <Input
                id="cm-cash-pct"
                type="number"
                min="0"
                max="100"
                step="0.01"
                inputMode="decimal"
                value={cashPercent}
                onChange={(e) => setCashPercent(e.target.value)}
                disabled={busy}
                placeholder="10"
              />
            </FormField>
          )}

          {/* D16 — never a block. Next and Save both stay enabled underneath it. */}
          {cashExceedsDown && (
            <p
              role="status"
              data-testid="commission-model-cash-warning"
              className="m-0 rounded-md border border-status-warning/40 bg-status-warning/10 p-space-3 text-small text-onyx"
            >
              Cash at closing is more than the down payment — the seller would be
              paying you more than the buyer brought to the table. That may be
              exactly what you meant; it is worth a second look either way.
            </p>
          )}

          <FormField
            label={textOrUndefined(copy.saleLabel)}
            htmlFor="cm-sale"
            helperText={textOrUndefined(copy.saleHelper)}
            error={
              saleFeePercent.trim() !== "" && !saleValid
                ? "Must be a percent under 100."
                : undefined
            }
          >
            <Input
              id="cm-sale"
              type="number"
              min="0"
              max="100"
              step="0.01"
              inputMode="decimal"
              value={saleFeePercent}
              onChange={(e) => setSaleFeePercent(e.target.value)}
              disabled={busy}
              placeholder="2"
            />
          </FormField>

          {lifetimeExample && (
            <WorkedExample testId="commission-model-lifetime-example">
              {lifetimeExample}
            </WorkedExample>
          )}
            </>
          )}

          {step === "review" && (
            <div
              className="flex flex-col gap-space-3"
              data-testid="commission-model-review"
            >
              {reviewGroups.map((group) => (
                <section
                  key={group.step}
                  className="rounded-md border border-border-light bg-white-warm p-space-3"
                >
                  <div className="flex items-baseline justify-between gap-space-3">
                    <h4 className="m-0 text-caption uppercase tracking-wide text-gray-mid">
                      {STEP_COPY[STEP_KEYS[group.step]].label}
                    </h4>
                    <Button
                      variant="text"
                      onClick={() => setStepIndex(group.step)}
                      disabled={busy}
                      data-testid={`commission-model-change-${group.step}`}
                    >
                      Change
                    </Button>
                  </div>
                  <dl className="m-0 mt-space-2 grid gap-x-space-4 gap-y-space-1 sm:grid-cols-[minmax(0,auto)_minmax(0,1fr)]">
                    {group.rows.map((row, i) => (
                      <div key={`${i}-${row.label}`} className="contents">
                        {/* A blank published label drops its <dt> (D4); the
                            answer stays, spanning both columns. */}
                        {hasText(row.label) && (
                          <dt className="m-0 text-small text-gray-mid">{row.label}</dt>
                        )}
                        <dd
                          className={cn(
                            "m-0 text-body text-onyx",
                            !hasText(row.label) && "sm:col-span-2",
                          )}
                        >
                          {row.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>
              ))}
            </div>
          )}

          {error && (
            <p className="m-0 text-small text-status-error" role="alert">
              {error}
            </p>
          )}
          <SubmittingHint pending={busy} message="Saving…" />
        </div>

        {/* The live figures, and the terms they are computed against. Both are
            the server's — the panel is a renderer, not a calculator. */}
        <aside
          aria-label="Live preview"
          className="flex flex-col gap-space-4 rounded-lg border border-border-light bg-white-warm p-space-4"
        >
          <h3 className="m-0 font-display text-lead font-bold text-onyx">
            What you&rsquo;d earn
          </h3>
          {preview ? (
            <>
              <HeadlineFigures result={preview} compact />
              <TermsPanel terms={preview.terms} />
            </>
          ) : (
            <p className="m-0 text-small text-gray-mid">
              Fill in the deal size and your fee and the numbers show up here.
            </p>
          )}
          {previewError && (
            <p className="m-0 text-caption text-gray-mid" role="status">
              {previewError}
            </p>
          )}
        </aside>
      </div>
    </Modal>
  );
}
